// ============================================================================
//  patent-viewer.js — fullscreen deep-zoom viewer for the 1N diagram (OpenSeadragon)
//
//  A control near the inline diagram opens an accessible <dialog> containing an
//  OpenSeadragon instance. Native <dialog>.showModal() provides the modal
//  semantics and focus containment; we add Esc-to-close, a close button, and
//  focus restoration to the trigger. OpenSeadragon is imported lazily on first
//  open so it isn't in the initial bundle.
// ============================================================================

// High-resolution export of the 1N diagram for the fullscreen deep-zoom view.
// Imported so Vite fingerprints it into build/assets/ (dev and prod correct).
import diagramFull from '../../../images/AO1N-Graphic_4x.png';

export function initPatentViewer() {
  const openBtn = document.querySelector('[data-viewer-open]');
  const dialog = document.querySelector('[data-viewer-dialog]');
  if (!openBtn || !dialog) return;

  const closeBtn = dialog.querySelector('[data-viewer-close]');
  const canvas = dialog.querySelector('[data-viewer-canvas]');
  const zoomInBtn = dialog.querySelector('[data-viewer-zoom-in]');
  const zoomOutBtn = dialog.querySelector('[data-viewer-zoom-out]');
  let viewer = null;
  let lastFocused = null;

  const zoom = (factor) => {
    if (!viewer || !viewer.viewport) return;
    viewer.viewport.zoomBy(factor);
    viewer.viewport.applyConstraints();
  };
  zoomInBtn?.addEventListener('click', () => zoom(1.5));
  zoomOutBtn?.addEventListener('click', () => zoom(1 / 1.5));

  const open = async () => {
    lastFocused = document.activeElement;

    // Prefer the native modal dialog; fall back to a plain open attribute.
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');

    if (!viewer) {
      const { default: OpenSeadragon } = await import('openseadragon');

      viewer = OpenSeadragon({
        element: canvas,
        // Full-size diagram export (fingerprinted via the import above).
        tileSources: { type: 'image', url: diagramFull },
        // OpenSeadragon's button sprites aren't bundled (self-hosted, no CDN),
        // so hide them and rely on wheel/drag/double-click zoom + the navigator
        // minimap and keyboard (+/- and arrows).
        showNavigationControl: false,
        showNavigator: true,
        navigatorPosition: 'BOTTOM_RIGHT',
        gestureSettingsMouse: { clickToZoom: false, scrollToZoom: true },
        visibilityRatio: 1,
        minZoomImageRatio: 0.9,
        // Allow zooming well past native size (default caps at ~1.1×) so fine
        // detail can be inspected far beyond 100%.
        maxZoomPixelRatio: 5,
        constrainDuringPan: true,
        animationTime: 0.6,
      });
    } else {
      // Recompute size after the dialog becomes visible.
      viewer.viewport.goHome(true);
    }

    // Give the canvas a tick to lay out, then resize.
    requestAnimationFrame(() => viewer && viewer.viewport && viewer.viewport.goHome(true));

    closeBtn?.focus();
  };

  const close = () => {
    if (dialog.open) dialog.close?.();
    dialog.removeAttribute('open');
    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
  };

  openBtn.addEventListener('click', open);
  closeBtn?.addEventListener('click', close);

  // Esc: the native dialog fires `cancel`; keep focus restoration consistent.
  dialog.addEventListener('cancel', (e) => {
    e.preventDefault();
    close();
  });
  // Clicking the backdrop (outside the dialog content) closes it.
  dialog.addEventListener('click', (e) => {
    if (e.target === dialog) close();
  });
}
